import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { InboundEmailConfig } from './EmailConfiguration';
interface InboundEmailFormProps {
  config: InboundEmailConfig | null;
  onSave: (config: Partial<InboundEmailConfig>) => void;
  onCancel: () => void;
  locale: string;
}
export function InboundEmailForm({
  config,
  onSave,
  onCancel,
  locale
}: InboundEmailFormProps) {
  const [formData, setFormData] = useState<Partial<InboundEmailConfig>>({
    scopeLevel: 'Tenant',
    emailAddress: '',
    imapHost: '',
    imapPort: 993,
    useSSL: true,
    username: '',
    password: '',
    pollingFrequencyMinutes: 15,
    defaultDocumentType: 'Invoice',
    defaultTags: [],
    acceptEmailBodyAsAttachment: false,
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testingConnection, setTestingConnection] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  // Sample entities and sites for the form
  const entities = [{
    id: 'ent-001',
    name: 'Global Technologies Inc.'
  }, {
    id: 'ent-002',
    name: 'European Operations Ltd'
  }];
  const sites = [{
    id: 'site-001',
    name: 'New York HQ',
    entityId: 'ent-001'
  }, {
    id: 'site-002',
    name: 'San Francisco Office',
    entityId: 'ent-001'
  }, {
    id: 'site-003',
    name: 'London Office',
    entityId: 'ent-002'
  }, {
    id: 'site-004',
    name: 'Paris Branch',
    entityId: 'ent-002'
  }];
  // Document types
  const documentTypes = ['Invoice', 'Credit Note', 'Purchase Order', 'Receipt', 'Statement', 'Other'];
  // Initialize form with existing config data when editing
  useEffect(() => {
    if (config) {
      setFormData({
        ...config,
        // Don't include the rules, they're managed separately
        rules: undefined
      });
      if (config.scopeLevel === 'Site' && config.entityId) {
        setSelectedEntityId(config.entityId);
      }
    }
  }, [config]);
  // Filter sites based on selected entity
  const filteredSites = selectedEntityId ? sites.filter(site => site.entityId === selectedEntityId) : [];
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scopeLevel') {
      // Reset entity and site fields when scope changes
      setFormData({
        ...formData,
        scopeLevel: value as 'Tenant' | 'Entity' | 'Site',
        entityId: undefined,
        entityName: undefined,
        siteId: undefined,
        siteName: undefined
      });
      setSelectedEntityId(null);
    } else if (name === 'entityId') {
      const entity = entities.find(e => e.id === value);
      setSelectedEntityId(value || null);
      setFormData({
        ...formData,
        entityId: value || undefined,
        entityName: entity ? entity.name : undefined,
        siteId: undefined,
        siteName: undefined
      });
    } else if (name === 'siteId') {
      const site = sites.find(s => s.id === value);
      setFormData({
        ...formData,
        siteId: value || undefined,
        siteName: site ? site.name : undefined
      });
    } else if (name === 'imapPort') {
      setFormData({
        ...formData,
        [name]: parseInt(value, 10)
      });
    } else if (name === 'pollingFrequencyMinutes') {
      setFormData({
        ...formData,
        [name]: parseInt(value, 10)
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle tag input
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.defaultTags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        defaultTags: [...(formData.defaultTags || []), tagInput.trim()]
      });
      setTagInput('');
    }
  };
  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      defaultTags: formData.defaultTags?.filter(t => t !== tag)
    });
  };
  // Handle tag input key press (Enter)
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };
  // Test connection
  const handleTestConnection = () => {
    if (!formData.imapHost || !formData.username || !formData.password) {
      setErrors({
        ...errors,
        connectionTest: 'Please fill in server details, username and password before testing'
      });
      return;
    }
    setTestingConnection(true);
    // Simulate API call
    setTimeout(() => {
      setTestingConnection(false);
      alert('Connection successful! Email server is reachable.');
    }, 1500);
  };
  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.emailAddress) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }
    if (!formData.imapHost) {
      newErrors.imapHost = 'IMAP host is required';
    }
    if (!formData.imapPort) {
      newErrors.imapPort = 'IMAP port is required';
    }
    if (!formData.username) {
      newErrors.username = 'Username is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    if (formData.scopeLevel === 'Entity' && !formData.entityId) {
      newErrors.entityId = 'Please select an entity';
    }
    if (formData.scopeLevel === 'Site') {
      if (!formData.entityId) {
        newErrors.entityId = 'Please select an entity';
      }
      if (!formData.siteId) {
        newErrors.siteId = 'Please select a site';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15183">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-15184">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15185">
          {config ? 'Edit Email Configuration' : 'Add Email Configuration'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15186">
          {config ? 'Update the settings for polling this email account' : 'Configure a new email account to poll for incoming documents'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-15187">
        <div className="px-4 py-5 sm:p-6" data-id="element-15188">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15189">
            {/* Scope Selection */}
            <div className="sm:col-span-3" data-id="element-15190">
              <label htmlFor="scopeLevel" className="block text-sm font-medium text-gray-700" data-id="element-15191">
                Configuration Scope
              </label>
              <select id="scopeLevel" name="scopeLevel" value={formData.scopeLevel || 'Tenant'} onChange={handleInputChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-15192">
                <option value="Tenant" data-id="element-15193">Tenant-wide</option>
                <option value="Entity" data-id="element-15194">Entity-specific</option>
                <option value="Site" data-id="element-15195">Site-specific</option>
              </select>
              <p className="mt-1 text-xs text-gray-500" data-id="element-15196">
                Determines the scope of this email configuration
              </p>
            </div>
            {/* Entity Selection - shown for Entity and Site scopes */}
            {(formData.scopeLevel === 'Entity' || formData.scopeLevel === 'Site') && <div className="sm:col-span-3" data-id="element-15197">
                <label htmlFor="entityId" className="block text-sm font-medium text-gray-700" data-id="element-15198">
                  Entity
                </label>
                <select id="entityId" name="entityId" value={formData.entityId || ''} onChange={handleInputChange} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${errors.entityId ? 'border-red-300' : ''}`} data-id="element-15199">
                  <option value="" data-id="element-15200">Select an entity</option>
                  {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-15201">
                      {entity.name}
                    </option>)}
                </select>
                {errors.entityId && <p className="mt-1 text-sm text-red-600" data-id="element-15202">{errors.entityId}</p>}
              </div>}
            {/* Site Selection - shown only for Site scope */}
            {formData.scopeLevel === 'Site' && <div className="sm:col-span-3" data-id="element-15203">
                <label htmlFor="siteId" className="block text-sm font-medium text-gray-700" data-id="element-15204">
                  Site
                </label>
                <select id="siteId" name="siteId" value={formData.siteId || ''} onChange={handleInputChange} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${errors.siteId ? 'border-red-300' : ''}`} disabled={!selectedEntityId} data-id="element-15205">
                  <option value="" data-id="element-15206">Select a site</option>
                  {filteredSites.map(site => <option key={site.id} value={site.id} data-id="element-15207">
                      {site.name}
                    </option>)}
                </select>
                {!selectedEntityId && <p className="mt-1 text-xs text-gray-500" data-id="element-15208">
                    Please select an entity first
                  </p>}
                {errors.siteId && <p className="mt-1 text-sm text-red-600" data-id="element-15209">{errors.siteId}</p>}
              </div>}
            {/* Email Configuration */}
            <div className="sm:col-span-6 border-t border-gray-200 pt-5" data-id="element-15210">
              <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-15211">
                Email Account Details
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15212">
                Configure the email account that will be polled for documents
              </p>
            </div>
            <div className="sm:col-span-4" data-id="element-15213">
              <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700" data-id="element-15214">
                Email Address
              </label>
              <input type="email" name="emailAddress" id="emailAddress" value={formData.emailAddress || ''} onChange={handleInputChange} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.emailAddress ? 'border-red-300' : ''}`} placeholder="e.g., invoices@example.com" data-id="element-15215" />
              {errors.emailAddress && <p className="mt-1 text-sm text-red-600" data-id="element-15216">
                  {errors.emailAddress}
                </p>}
            </div>
            <div className="sm:col-span-3" data-id="element-15217">
              <label htmlFor="imapHost" className="block text-sm font-medium text-gray-700" data-id="element-15218">
                IMAP Server Host
              </label>
              <input type="text" name="imapHost" id="imapHost" value={formData.imapHost || ''} onChange={handleInputChange} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.imapHost ? 'border-red-300' : ''}`} placeholder="e.g., imap.gmail.com" data-id="element-15219" />
              {errors.imapHost && <p className="mt-1 text-sm text-red-600" data-id="element-15220">{errors.imapHost}</p>}
            </div>
            <div className="sm:col-span-3" data-id="element-15221">
              <label htmlFor="imapPort" className="block text-sm font-medium text-gray-700" data-id="element-15222">
                IMAP Port
              </label>
              <input type="number" name="imapPort" id="imapPort" value={formData.imapPort || ''} onChange={handleInputChange} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.imapPort ? 'border-red-300' : ''}`} placeholder="993" data-id="element-15223" />
              {errors.imapPort && <p className="mt-1 text-sm text-red-600" data-id="element-15224">{errors.imapPort}</p>}
            </div>
            <div className="sm:col-span-3" data-id="element-15225">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700" data-id="element-15226">
                Username
              </label>
              <input type="text" name="username" id="username" value={formData.username || ''} onChange={handleInputChange} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.username ? 'border-red-300' : ''}`} placeholder="Email username or address" data-id="element-15227" />
              {errors.username && <p className="mt-1 text-sm text-red-600" data-id="element-15228">{errors.username}</p>}
            </div>
            <div className="sm:col-span-3" data-id="element-15229">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-15230">
                Password
              </label>
              <input type="password" name="password" id="password" value={formData.password || ''} onChange={handleInputChange} className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${errors.password ? 'border-red-300' : ''}`} placeholder="Email password or app password" data-id="element-15231" />
              {errors.password && <p className="mt-1 text-sm text-red-600" data-id="element-15232">{errors.password}</p>}
              <p className="mt-1 text-xs text-gray-500" data-id="element-15233">
                For Gmail or Microsoft 365, you may need to use an app-specific
                password
              </p>
            </div>
            <div className="sm:col-span-3" data-id="element-15234">
              <div className="flex items-center h-5" data-id="element-15235">
                <input id="useSSL" name="useSSL" type="checkbox" checked={formData.useSSL} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-15236" />
                <label htmlFor="useSSL" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-15237">
                  Use SSL/TLS (recommended)
                </label>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-15238">
              <button type="button" onClick={handleTestConnection} disabled={testingConnection} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15239">
                <div className="h-4 w-4 mr-2" data-id="element-15240" />
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </button>
              {errors.connectionTest && <p className="mt-1 text-sm text-red-600" data-id="element-15241">
                  {errors.connectionTest}
                </p>}
            </div>
            {/* Polling Configuration */}
            <div className="sm:col-span-6 border-t border-gray-200 pt-5" data-id="element-15242">
              <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-15243">
                Polling Configuration
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15244">
                Configure how often to check for new emails and how to process
                them
              </p>
            </div>
            <div className="sm:col-span-3" data-id="element-15245">
              <label htmlFor="pollingFrequencyMinutes" className="block text-sm font-medium text-gray-700" data-id="element-15246">
                Polling Frequency (minutes)
              </label>
              <select id="pollingFrequencyMinutes" name="pollingFrequencyMinutes" value={formData.pollingFrequencyMinutes || 15} onChange={handleInputChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-15247">
                <option value={5} data-id="element-15248">Every 5 minutes</option>
                <option value={10} data-id="element-15249">Every 10 minutes</option>
                <option value={15} data-id="element-15250">Every 15 minutes</option>
                <option value={30} data-id="element-15251">Every 30 minutes</option>
                <option value={60} data-id="element-15252">Every hour</option>
                <option value={120} data-id="element-15253">Every 2 hours</option>
              </select>
            </div>
            <div className="sm:col-span-3" data-id="element-15254">
              <label htmlFor="defaultDocumentType" className="block text-sm font-medium text-gray-700" data-id="element-15255">
                Default Document Type
              </label>
              <select id="defaultDocumentType" name="defaultDocumentType" value={formData.defaultDocumentType || 'Invoice'} onChange={handleInputChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-15256">
                {documentTypes.map(type => <option key={type} value={type} data-id="element-15257">
                    {type}
                  </option>)}
              </select>
              <p className="mt-1 text-xs text-gray-500" data-id="element-15258">
                Document type assigned when no rules match
              </p>
            </div>
            <div className="sm:col-span-6" data-id="element-15259">
              <label htmlFor="defaultTags" className="block text-sm font-medium text-gray-700" data-id="element-15260">
                Default Tags
              </label>
              <div className="mt-1 flex rounded-md shadow-sm" data-id="element-15261">
                <input type="text" name="tagInput" id="tagInput" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyPress={handleTagKeyPress} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" placeholder="Add a tag and press Enter" data-id="element-15262" />
                <button type="button" onClick={handleAddTag} className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-r-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15263">
                  Add
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-15264">
                {formData.defaultTags?.map(tag => <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-15265">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none" data-id="element-15266">
                      <span className="sr-only" data-id="element-15267">Remove tag {tag}</span>
                      <XIcon className="h-3 w-3" data-id="element-15268" />
                    </button>
                  </span>)}
                {(!formData.defaultTags || formData.defaultTags.length === 0) && <span className="text-xs text-gray-500 italic" data-id="element-15269">
                    No default tags set
                  </span>}
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-15270">
                Tags applied to all documents from this email account
              </p>
            </div>
            <div className="sm:col-span-6" data-id="element-15271">
              <div className="flex items-start" data-id="element-15272">
                <div className="flex items-center h-5" data-id="element-15273">
                  <input id="acceptEmailBodyAsAttachment" name="acceptEmailBodyAsAttachment" type="checkbox" checked={formData.acceptEmailBodyAsAttachment} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-15274" />
                </div>
                <div className="ml-3 text-sm" data-id="element-15275">
                  <label htmlFor="acceptEmailBodyAsAttachment" className="font-medium text-gray-700" data-id="element-15276">
                    Save email body as attachment
                  </label>
                  <p className="text-gray-500" data-id="element-15277">
                    When enabled, the email body will be saved as a text file
                    attachment
                  </p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-15278">
              <div className="flex items-start" data-id="element-15279">
                <div className="flex items-center h-5" data-id="element-15280">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-15281" />
                </div>
                <div className="ml-3 text-sm" data-id="element-15282">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-15283">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-15284">
                    When enabled, this email account will be polled for new
                    documents
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-15285">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15286">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-15287" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15288">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-15289" />
            {config ? 'Update Configuration' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>;
}